import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation, Language } from '../i18n/languageManager';
import { languages, LanguageManager } from '../i18n/languages';

type LanguageContextType = {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string, namespace?: string) => string;
  languageManager: LanguageManager;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [lang, setLang] = useState<Language>(() => {
    // Get language from localStorage or use browser language or default to 'tr'
    const savedLang = localStorage.getItem('preferredLanguage') as Language;
    const browserLang = navigator.language.split('-')[0] as Language;
    // Default to 'tr' if no saved preference
    return savedLang || (['en', 'tr', 'es', 'de', 'zh', 'ja', 'ko'].includes(browserLang) ? browserLang : 'en');
  });

  // Save language preference to localStorage and update document HTML lang attribute when it changes
  useEffect(() => {
    localStorage.setItem('preferredLanguage', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const { t } = useTranslation(lang);

  // Map state lang code to display name for languages object
  const langKeyMap: Record<string, string> = {
    'tr': 'Türkçe',
    'en': 'English',
    'es': 'Español',
    'de': 'Deutsch',
    'zh': '中文',
    'ja': '日本語',
    'ko': 'Korean'
  };

  const currentManager = languages[langKeyMap[lang] || 'English'];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, languageManager: currentManager }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
